import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../../../web/ReactQueryProvider';
import { AIDA_ASSISTANT_NAME } from '../../../../hooks/useAISummary';
import { PracticeCallViewData } from '../components/PracticeCallRow';
import { AnalysisType } from '../types';

interface UsePracticeCallAnalysisProps {
	selectedCalls: PracticeCallViewData[];
	companyId?: string;
	source?: string;
}

/**
 * Hook for analyzing practice calls - adapted from useMultiCallAnalysis
 * for the IPhoneCallView data structure used by practice calls
 */
export const usePracticeCallAnalysis = ({ selectedCalls, companyId, source }: UsePracticeCallAnalysisProps) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('usePracticeCallAnalysis');
	const userSession = useUserSession();

	const [selectedAnalysisType, setSelectedAnalysisType] = React.useState<AnalysisType | null>(null);
	const [customSystemMessage, setCustomSystemMessage] = React.useState<string>('');
	const [isAnalysisModalOpen, setIsAnalysisModalOpen] = React.useState(false);
	const [aiResponse, setAiResponse] = React.useState('');
	const [conversationId, setConversationId] = React.useState<string>(null);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);
	const [combinedTranscriptionText, setCombinedTranscriptionText] = React.useState<string>('');
	const [hasStartedAnalysis, setHasStartedAnalysis] = React.useState<boolean>(false);
	const [isLoadingTranscriptions, setIsLoadingTranscriptions] = React.useState<boolean>(false);

	const [conversationMessages, setConversationMessages] = React.useState<
		{ role: 'user' | 'assistant'; content: string }[]
	>([]);
	const [isNewAnalysisSession, setIsNewAnalysisSession] = React.useState<boolean>(true);

	// Fetch all transcriptions for selected practice calls
	const fetchAllTranscriptions = React.useCallback(async () => {
		if (selectedCalls.length === 0) return;

		setIsLoadingTranscriptions(true);
		try {
			const transcriptionPromises = selectedCalls.map(async call => {
				if (!call.phoneCall.transcriptionId) return '';

				try {
					const response = await userSession.webServiceHelper.callAsync<any>(
						Api.ImpersonationBroker.composeApiUrl({
							queryParams: { type: 'SpeakerLine' },
							urlPath: `transcription/${call.phoneCall.transcriptionId}/view`,
						}),
						'GET'
					);

					if (!response?.speakerLines) return '';

					// Format the transcription text from speaker lines
					const formattedText = response.speakerLines
						.map((line: Api.ISpeakerLine) => `${line.speaker}: ${line.line}`)
						.join('\n');

					// For practice calls, use the first contact or a default
					const contact = call.contacts?.[0];
					const contactName = contact ? `${contact.firstName} ${contact.lastName}` : 'Practice Contact';
					const companyName = call.company?.companyName || 'Practice Company';

					// Build simplified context for practice calls
					const contextParts: string[] = [];
					contextParts.push(`Call Duration: ${Math.round((call.phoneCall.duration || 0) / 1000)}s`);
					contextParts.push(`Direction: ${call.phoneCall.direction || 'Unknown'}`);
					contextParts.push(`Status: ${call.phoneCall.callStatus || 'Unknown'}`);
					if (contactName) {
						contextParts.push(`Contact: ${contactName}`);
					}
					if (companyName) {
						contextParts.push(`Company: ${companyName}`);
					}
					if (call.phoneCall.startTime) {
						const callDate = new Date(call.phoneCall.startTime).toISOString().split('T')[0];
						const callTime = new Date(call.phoneCall.startTime).toLocaleTimeString();
						contextParts.push(`Call Date: ${callDate}`);
						contextParts.push(`Call Time: ${callTime}`);
					}
					contextParts.push('Call Type: Practice Call');

					const contextText = contextParts.join('\n');

					return `Practice Call with ${contactName} at ${companyName}:\n\nCall Context:\n${contextText}\n\nTranscription:\n${formattedText}`;
				} catch (error) {
					console.error(`Failed to fetch transcription for practice call ${call.phoneCall.id}:`, error);
					return '';
				}
			});

			const transcriptions = await Promise.all(transcriptionPromises);
			const validTranscriptions = transcriptions.filter((t: string) => t.length > 0);
			const combined = validTranscriptions.join('\n\n-------end call--------\n\n');
			setCombinedTranscriptionText(combined);
		} catch (error) {
			console.error('Failed to fetch transcriptions:', error);
			errorMessages.pushApiError(error);
		} finally {
			setIsLoadingTranscriptions(false);
		}
	}, [selectedCalls, userSession, errorMessages]);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: (conversation: any) => {
			setConversationId(conversation.id);
		},
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convoId ?? conversationId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event: any, _params: any) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				if (data.isDelta === false) {
					setAiResponse(data.message.text);
				} else {
					setAiResponse(prev => {
						return prev + messageText;
					});
				}

				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: conversationId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;

							updatedText = data.isDelta === false ? messageText : updatedText + messageText;

							return [
								...messages,
								{
									...lastAssistantMessage,
									text: updatedText,
								},
							];
						} else {
							return [
								...(messages || []),
								{
									id: data.message.id,
									role: Api.AIChatRole.Assistant,
									text: messageText,
									creationDate: new Date().toISOString(),
								},
							];
						}
					}
				);
			}
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPromptMutation', error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	// Effect to send analysis message when transcriptions and conversation are ready
	React.useEffect(() => {
		if (
			conversationId &&
			selectedAnalysisType &&
			combinedTranscriptionText &&
			!hasFetchedFeedback &&
			!hasStartedAnalysis &&
			!aiPromptMutation.isLoading
		) {
			setHasStartedAnalysis(true);

			const chatMessages: {
				role: Api.AIChatRole;
				text: string;
				id: string | null;
			}[] = [
				{
					role: Api.AIChatRole.User,
					text: `Practice Call Transcriptions (${selectedCalls.length} calls):\n${combinedTranscriptionText}`,
					id: null,
				},
			];

			// Only include system message if custom message is provided
			if (customSystemMessage && customSystemMessage.trim()) {
				chatMessages.unshift({
					role: Api.AIChatRole.System,
					text: customSystemMessage,
					id: null,
				});
			}

			aiPromptMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				convoId: conversationId,
				chatRequest: {
					chatMessages,
				},
			});
		}
	}, [
		conversationId,
		selectedAnalysisType,
		combinedTranscriptionText,
		hasFetchedFeedback,
		hasStartedAnalysis,
		aiPromptMutation,
		selectedCalls.length,
		customSystemMessage,
	]);

	// Start analysis for practice calls
	const startMultiAnalysis = React.useCallback(
		async (analysisType: AnalysisType, systemMessage?: string) => {
			setSelectedAnalysisType(analysisType);
			setCustomSystemMessage(systemMessage || '');
			setIsAnalysisModalOpen(true);
			setConversationMessages([]);
			setAiResponse('');
			setHasFetchedFeedback(false);
			setHasStartedAnalysis(false);
			setCombinedTranscriptionText('');
			setIsNewAnalysisSession(true);

			// Fetch all transcriptions first
			await fetchAllTranscriptions();

			// Create conversation and start analysis
			aiAssistantCreateConversationMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				phoneCallId: selectedCalls[0]?.phoneCall?.id,
				companyId,
				leadServedId: undefined,
				source,
			});
		},
		[selectedCalls, companyId, source, fetchAllTranscriptions, aiAssistantCreateConversationMutation]
	);

	const closeAnalysis = React.useCallback(() => {
		setIsAnalysisModalOpen(false);
		setSelectedAnalysisType(null);
		setConversationMessages([]);
		setConversationId(null);
		setAiResponse('');
		setHasFetchedFeedback(false);
		setHasStartedAnalysis(false);
		setCombinedTranscriptionText('');
		setIsNewAnalysisSession(true);
	}, []);

	// Track when AI response is complete
	const [lastAiResponseCompleted, setLastAiResponseCompleted] = React.useState(false);
	React.useEffect(() => {
		if (!aiPromptMutation.isLoading && aiResponse && hasFetchedFeedback && !lastAiResponseCompleted) {
			setConversationMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
			setLastAiResponseCompleted(true);
		}
	}, [aiPromptMutation.isLoading, aiResponse, hasFetchedFeedback, lastAiResponseCompleted]);

	// Reset tracking when starting a new AI session
	React.useEffect(() => {
		if (!hasFetchedFeedback && isNewAnalysisSession) {
			setLastAiResponseCompleted(false);
			setConversationMessages([]);
			setIsNewAnalysisSession(false);
		} else if (!hasFetchedFeedback && !isNewAnalysisSession) {
			setLastAiResponseCompleted(false);
		}
	}, [hasFetchedFeedback, isNewAnalysisSession]);

	const sendFollowUp = React.useCallback(
		(question: string) => {
			if (!question.trim() || !conversationId) return;

			setConversationMessages(prev => [...prev, { role: 'user', content: question }]);

			setAiResponse('');
			setHasFetchedFeedback(false);

			aiPromptMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				convoId: conversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: question,
							id: null,
						},
					],
				},
			});

			setLastAiResponseCompleted(false);
		},
		[conversationId, aiPromptMutation]
	);

	return {
		selectedAnalysisType,
		isAnalysisModalOpen,
		startMultiAnalysis,
		closeAnalysis,
		aiResponse,
		isLoading: aiAssistantCreateConversationMutation.isPending || aiPromptMutation.isLoading || isLoadingTranscriptions,
		conversationMessages,
		conversationId,
		sendFollowUp,
		lastAiResponseCompleted,
		selectedCallCount: selectedCalls.length,
	};
};
